import { Lightbulb, type LucideIcon } from "lucide-react";
import type { SubjectKind } from "./api";
import { ENTITY_TYPES } from "./entityTypes";

/**
 * The nine kinds a world-view subject can be (UI-10): the eight entity types, wearing the
 * same icons and words they wear in the entity manager, plus `topic`.
 *
 * `topic` is the one that is not an entity, and it is deliberate: "the Q3 pricing decision"
 * is a subject the world view legitimately holds beliefs about, and making every subject an
 * entity would force synthesis to mint entities as a side effect — putting a machine on the
 * wrong side of the entity manager's human gate.
 *
 * Reusing `ENTITY_TYPES` rather than restating it means a subject that *is* Acme wears the
 * same icon on its world-view card as on its entity row, which is the whole reason the kind
 * vocabulary was shared in the first place.
 */
export interface SubjectKindMeta {
  kind: SubjectKind;
  /** Singular, for the page's kind eyebrow. */
  label: string;
  /** Plural, for the navigator's "By subject type" rows. */
  plural: string;
  icon: LucideIcon;
}

/**
 * Where the world view's word differs from the entity manager's. The manager says
 * "Persons" because it is a table of typed rows; the World view panel says **People**, and
 * the handoff is the authority for its own panel.
 */
const WORLD_VIEW_PLURAL: Partial<Record<SubjectKind, string>> = { person: "People" };

export const SUBJECT_KINDS: SubjectKindMeta[] = [
  // `term` is a word, never a page (M15.2), so it is not a kind a subject can be.
  ...ENTITY_TYPES.filter((meta) => meta.type !== "term").map((meta) => ({
    kind: meta.type as SubjectKind,
    label: meta.label,
    plural: WORLD_VIEW_PLURAL[meta.type] ?? meta.plural,
    icon: meta.icon,
  })),
  { kind: "topic", label: "Topic", plural: "Topics", icon: Lightbulb },
];

const BY_KIND = new Map(SUBJECT_KINDS.map((meta) => [meta.kind, meta]));

export function subjectKindMeta(kind: SubjectKind): SubjectKindMeta {
  return BY_KIND.get(kind) ?? SUBJECT_KINDS[SUBJECT_KINDS.length - 1]!;
}

/**
 * The order the navigator's "By subject type" section lists kinds in, from the handoff:
 * customers, projects, people, products, suppliers, events — "projects" here is the
 * handoff's own word for the position, filled by `initiative` since 0044's rename (M16.1).
 *
 * It is deliberately shorter than the nine. The panel is built by walking this list and
 * then appending any other kind that actually has subjects — so it matches the design when
 * the data matches the design, and a `topic` page is never unreachable because the drawing
 * did not anticipate one.
 */
export const NAVIGATOR_KIND_ORDER: SubjectKind[] = [
  "customer",
  "initiative",
  "person",
  "team",
  "product",
  "supplier",
  "event",
];
